/**
 * LabeledSwitch Component
 * Standardised on/off toggle: a Switch on the left with the label and an optional
 * caption in a fixed-gutter content column, so the caption always aligns under the
 * label (never under the switch). Use this for every boolean toggle instead of an
 * ad-hoc FormControlLabel + Switch + Typography so spacing/indent stays consistent.
 *
 * Controlled + presentational (no react-hook-form coupling) so it drops straight
 * into a Controller render or any local-state toggle.
 */

import React from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';

// Width of the switch + its internal padding, so the caption in row 2 lines up
// under the label text rather than under the switch.
const GUTTER = 46;

function LabeledSwitch({
  checked = false,
  onChange,
  label,
  caption,
  disabled = false,
  sx,
  ...switchProps
}) {
  return (
    <Box sx={sx}>
      <FormControlLabel
        sx={{ ml: 0, mr: 0 }}
        disabled={disabled}
        control={
          <Switch
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...switchProps}
          />
        }
        label={label}
      />
      {caption != null && caption !== '' && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ ml: `${GUTTER}px`, mt: -0.25 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}

// A LabeledSwitch sized to sit on the same baseline as the labelled inputs beside
// it — for a "gate" toggle that occupies a grid column next to the fields it reveals
// (e.g. Combined payment | Interest freq | Capital freq). The minHeight/flex centering
// lines the switch up with sibling TextFields (which have label space above), instead
// of floating to the top of the row. Use this ONLY for inline gate toggles; grouped
// booleans belong in a ToggleGroup.
export function InlineGateToggle({ sx, ...props }) {
  return (
    <LabeledSwitch
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 56, ...sx }}
      {...props}
    />
  );
}

export default LabeledSwitch;
